import { z } from 'zod';

/** Escopo do token emitido para o CS ver o app como o viajante (spec 2026-10-12). */
export const VIEW_AS_TOKEN_SCOPE = 'view-as' as const;
/** Validade da concessão e do token, em segundos. */
export const VIEW_AS_TTL_S = 30 * 60;

/**
 * Operações do app-integration que um token view-as pode chamar. Fase 1: leitura, as
 * buscas de voo e hotel até o checkout e o evento de tela. Fora daqui o portão nega.
 * A ordem importa: literal antes de parâmetro (`/problem-reports/latest` antes de
 * `/problem-reports/:id`). A carteira (`/voll-wallet/*`) NÃO é lida no ver-como: o suporte
 * deixou de ver saldo, extrato, cartão e regras do viajante (P2-2; a UI já esconde o fluxo
 * nesse modo e os dados são financeiros). Rota de carteira nova entra em
 * `VIEW_AS_DENIED_OPERATIONS` (app-integration). `/stream` fica fora (o hub derrubaria a conexão ao vivo do
 * viajante), `/rating/request` consome o convite de NPS e os `/link` emitem credencial.
 */
export const VIEW_AS_ALLOWED_OPERATIONS = [
  'GET /me',
  'GET /me/workspaces',
  'GET /profile',
  'GET /profile/documents',
  'GET /profile/countries',
  'GET /profile/airlines',
  'GET /travel-preferences',
  'GET /travel-preferences/bus',
  'GET /travel-preferences/hotel-loyalty',
  'GET /travel-preferences/rental-loyalty',
  'GET /travel-preferences/rental',
  'GET /notification-preferences',
  'GET /history',
  'GET /conversations',
  'GET /conversations/column',
  'GET /support-tickets',
  'GET /notifications',
  'GET /notifications/counts',
  'GET /trips',
  'GET /trips/:tripId',
  'GET /vouchers',
  'GET /vouchers/:voucherCode/ancillaries',
  'GET /approvals',
  'GET /requested-trips',
  'GET /problem-reports',
  'GET /problem-reports/latest',
  'GET /problem-reports/eligibility',
  'GET /problem-reports/unread',
  'GET /problem-reports/:id',
  // Reembolso de KM: as leituras. O rascunho sob view-as vem do gravado, sem o `summary` (que grava).
  'GET /km-refund/options',
  'GET /km-refund/places',
  'GET /km-refund/places/reverse',
  'GET /km-refund/places/recent',
  'GET /km-refund/places/route',
  'GET /km-refund/draft',
  'GET /km-refund/transit',
  // Agendamento de carro (#6797): o catálogo, os recentes e o drawer do voucher do próprio viajante.
  'GET /mobility/schedule/options',
  'GET /mobility/schedule/places/recent',
  'GET /mobility/schedule/vouchers/:voucherId',
  'GET /mobility/taxi99/active-ride',
  'GET /mobility/taxi99/defaults',
  'GET /weather',
  'GET /places/distances',
  'GET /places/photo',
  'GET /places/:placeId',
  // Buscas de voo e hotel até o checkout (nunca emitem). As gravações de "recentes"
  // dessas rotas são puladas sob view-as, nas próprias rotas.
  'GET /flight/airports',
  'GET /flight/airports/history',
  'GET /flight/searches/recent',
  'GET /flight/planner-preview',
  'POST /flight/search',
  'POST /flight/refine-search',
  'POST /flight/return-search',
  'POST /flight/filter-text',
  'POST /flight/quote',
  'GET /flight/tariff',
  'GET /flight/legs',
  'GET /flight/segment-enrichment',
  'GET /flight/seat-map',
  'GET /business/booking-form',
  'GET /business/travelers',
  'GET /traveler-photos',
  'GET /business/cost-centers',
  'POST /business/budget',
  'GET /hotel/places',
  'GET /hotel/places/suggestions',
  'GET /hotel/home-rails',
  'GET /hotel/places/favorites',
  'POST /hotel/search-preview',
  'POST /hotel/search',
  'POST /hotel/search-page',
  'POST /hotel/stay-search',
  'POST /hotel/pricing',
  'POST /hotel/quote',
  'GET /hotel/details',
  'GET /hotel/offer',
  'POST /view-as/screen-events',
] as const;

export type ViewAsOperation = (typeof VIEW_AS_ALLOWED_OPERATIONS)[number];

export interface ViewAsOperationMatch {
  operation: ViewAsOperation;
  params: Record<string, string>;
}

function segments(path: string): string[] {
  return (path.split('?', 1)[0] ?? '').split('/').filter((s) => s !== '');
}

function matchSegments(pattern: string[], actual: string[]): Record<string, string> | null {
  if (pattern.length !== actual.length) return null;
  const params: Record<string, string> = {};
  for (const [i, p] of pattern.entries()) {
    const a = actual[i] ?? '';
    if (p.startsWith(':')) {
      try {
        params[p.slice(1)] = decodeURIComponent(a);
      } catch {
        return null;
      }
    } else if (p !== a) {
      return null;
    }
  }
  return params;
}

/** Casa `método + caminho` contra a lista; `null` = negado. Case-sensitive de propósito. */
export function matchViewAsOperation(method: string, path: string): ViewAsOperationMatch | null {
  const actual = segments(path);
  for (const operation of VIEW_AS_ALLOWED_OPERATIONS) {
    const [opMethod, opPath] = operation.split(' ') as [string, string];
    if (opMethod !== method) continue;
    const params = matchSegments(segments(opPath), actual);
    if (params) return { operation, params };
  }
  return null;
}

/** Telas do web-chat que viram evento de auditoria (rotas + overlays globais). */
export const VIEW_AS_SCREENS = [
  'home',
  'chat',
  'trip',
  'booking',
  'hotel_booking',
  'rental_booking',
  'bus_booking',
  'approvals',
  'requested',
  'profile',
  'report',
  'wallet',
  'mobility99',
  'not_found',
] as const;
export const ViewAsScreenSchema = z.enum(VIEW_AS_SCREENS);
export type ViewAsScreen = z.infer<typeof ViewAsScreenSchema>;

const AuditParamsSchema = z.record(z.string().max(40), z.string().max(200));

export const ViewAsScreenEventBodySchema = z
  .object({ screen: ViewAsScreenSchema, params: AuditParamsSchema })
  .strict();
export type ViewAsScreenEventBody = z.infer<typeof ViewAsScreenEventBodySchema>;

/** Wire do SNS app-integration → kanban (fila de auditoria). Nunca carrega conteúdo. */
export const TravelerAccessEventSchema = z
  .object({
    eventId: z.uuid(),
    grantId: z.uuid(),
    operatorId: z.string().min(1).max(64),
    travelerPublicId: z.string().min(1).max(64),
    kind: z.enum(['read', 'screen']),
    operation: z.string().min(1).max(120),
    params: AuditParamsSchema,
    at: z.coerce.date(),
  })
  .strict();
export type TravelerAccessEvent = z.infer<typeof TravelerAccessEventSchema>;

export const ViewAsMintRequestSchema = z
  .object({
    grantId: z.uuid(),
    operatorId: z.string().min(1).max(64),
    travelerPublicId: z.string().min(1).max(64),
    expiresAt: z.coerce.date(),
  })
  .strict();
export type ViewAsMintRequest = z.infer<typeof ViewAsMintRequestSchema>;

export const ViewAsMintResponseSchema = z
  .object({
    token: z.string().min(1),
    expiresAt: z.coerce.date(),
    // `false` = viajante sem binding do app: o token sai com o sub sintético `view-as:<publicId>`.
    // Ausente = app-integration anterior ao campo (o kanban trata como `true`).
    travelerOnApp: z.boolean().optional(),
  })
  .strict();
export type ViewAsMintResponse = z.infer<typeof ViewAsMintResponseSchema>;

export const ViewAsGrantStatusSchema = z.object({ active: z.boolean() }).strict();
export type ViewAsGrantStatus = z.infer<typeof ViewAsGrantStatusSchema>;
